import { useContext, useEffect, useState } from 'react'

import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import { useDecision } from '@optimizely/react-sdk'

import { OptimizelyContext } from '@optimizely/react-sdk'
import Button from '@space-core/components/button'
import Icon from '@space-core/components/icon'
import Modal from '@space-core/components/modal'
import ModalBody from '@space-core/components/modalBody'
import ModalFooter from '@space-core/components/modalFooter'
import ModalHeader from '@space-core/components/modalHeader'
import { HttpCodes } from '@space-core/constants'
import {
  Actions,
  Entities,
  EventNames,
  ScreenNames,
  CommunityTypeProductConsume,
  SourceNames,
  TryResult,
  FailureDescription,
  ComponentSource,
  Visibility,
  IsActivedValue
} from '@space-core/datahub'
import { DataHubEntities, DataHubService } from '@space-core/datahub/hotmartClub'
import { useDeviceInfo } from '@space-core/hooks/useDeviceInfo'
import useTranslation from '@space-core/hooks/useTranslation'
import { invalidateGroupCommunitiesEventDispatch } from '@space-events/invalidateGroupCommunitiesQuery'
import { toastEventDispatch } from '@space-events/toast'
import { ToastType, ToastPosition } from '@space-events/toast/enum'
import { toggleCommunityConfigurationSidebarEventDispatch } from '@space-events/toggleCommunityConfigurationSidebar'
import { useForm } from '@space-shell/common/hooks/useForm'
import type { CommunityProps, CreateCommunityProps } from '@space-shell/common/services/communities'
import { CommunitiesActions } from '@space-shell/common/services/communities'

import { UserInfoRole } from '@space-shell/common/services/user'
import { COMMUNITY_CREATION_SURVEY, useUserInfoStore } from '@space-shell/core/store'
import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'
import TryToCreateCommunitySchema from '@space-shell/modules/home/datahub/schemas/try/community/tryToCreateCommunitySchema'
import { useNavigate, useSearchParams } from 'react-router-dom'

import styled from 'styled-components'

import { COMMUNITY_TYPE } from '../../accessAndPrivacy/constants'
import { useCreateCommunity, useGetCommunity } from '../../hooks'

import useMenuCollapse from '../../hooks/useMenuCollapse'
import AccessAndPrivacyCommunity from '../components/accessAndPrivacyCommunity'
import BlockCommentsSelector from '../components/blockCommentsSelector/blockCommentsSelector'
import { HideCommunitySelector } from '../components/hideCommunitySelector'
import { ModerationCommunitySelector } from '../components/moderationCommunitySelector'
import { ReadOnlyCommunitySelector } from '../components/readyOnlyCommunitySelector'
import { formFieldsValidator } from '../constants'
import FormModal from '../formModal'
import type { CommunityModalProps, FormFields } from '../types'

function validateForm<T = unknown>(data: Record<keyof T, unknown>) {
  return Object.keys(data).every((key) => formFieldsValidator[key].isValid(data[key].value))
}

const CustomModal = styled(Modal)`
  --modal-max-width: 40rem;
`

const CommunityModalContent = ({
  isOpen,
  closeModal,
  modalType,
  communityId
}: CommunityModalProps) => {
  const deviceInfo = useDeviceInfo()
  const [searchParams] = useSearchParams()
  const { communitiesLength } = useMenuCollapse()
  const { sendDataHubConsume } = useSendDataHubConsume()
  const user = useUserInfoStore()
  const { t } = useTranslation()
  const { data, resetForm } = useForm<FormFields>()
  const navigate = useNavigate()
  const { createCommunityQuery, invalidateQueries } = useCreateCommunity()
  const { invalidateCommunityData } = useGetCommunity(communityId)
  const { optimizely } = useContext(OptimizelyContext)
  const [isValidFormData, setIsValidFormData] = useState<boolean>(false)
  const [{ enabled: enableModerationCommunity }] = useDecision(
    'com_adm_web_enable_permission_moderation_community'
  )

  const [{ enabled: isEnableCommunityCreationSurveyModal }] = useDecision(
    'com_adm_web_enable_community_creation_survey_modal'
  )

  const openCommunityConfigurationSidebar = (community: CommunityProps) => {
    toggleCommunityConfigurationSidebarEventDispatch({ action: 'open', community })
  }

  useEffect(() => {
    const validate = validateForm<FormFields>(data)
    setIsValidFormData(validate)
  }, [data])

  const handleResetForm = () => {
    invalidateCommunityData()
    resetForm()
    closeModal()
  }

  const handleClickCloseModal = (sendEventOnClose: boolean) => {
    if (modalType === CommunitiesActions.ADD && sendEventOnClose) {
      sendDataHubConsume({
        entity: Entities.COMMUNITY,
        action: Actions.CREATE,
        event: {
          eventName: EventNames.CLOSE_COMMUNITY_CREATE_MODAL,
          screenName: ScreenNames.COMMUNITY_CREATION_MODAL
        }
      })
    }

    handleResetForm()
  }

  const handleClick = () => {
    if (modalType === CommunitiesActions.ADD && isValidFormData) {
      const [createNewCommunity, source] = [
        Boolean(searchParams.get('createNewCommunity')),
        searchParams.get('source')
      ]
      const componentSource = createNewCommunity && source ? SourceNames[source] : null

      createCommunityQuery.mutate(
        {
          name: data.name.value,
          description: data.description.value,
          icon: data.icon.value,
          groupId: data.idGroup.value,
          type: data.communityType.value,
          hide: data.hide.value,
          collaborativeContent: data.collaborativeContent.value,
          defaultEnableComments: data.defaultEnableComments.value,
          isModerationCommunity: data.moderationCommunity.value || false
        } as CreateCommunityProps,
        {
          onSuccess: async (responseData) => {
            await invalidateQueries()
            invalidateGroupCommunitiesEventDispatch()
            isEnableCommunityCreationSurveyModal &&
              communitiesLength === 0 &&
              window.localStorage.setItem(COMMUNITY_CREATION_SURVEY, JSON.stringify(true))
            handleClickCloseModal(false)

            if (responseData && data.communityType.value === COMMUNITY_TYPE.EXCLUSIVE) {
              responseData.type = COMMUNITY_TYPE.EXCLUSIVE
              openCommunityConfigurationSidebar(responseData)
            }

            optimizely?.track(EventNames.TRY_CREATE_COMMUNITY)

            DataHubService.sendEvent({
              entity: DataHubEntities.Community,
              schema: new TryToCreateCommunitySchema({
                deviceInfo,
                userRole: user.role || UserInfoRole.VIEWER,
                communityId: responseData.id,
                communityName: responseData.name,
                communityType: CommunityTypeProductConsume[data.communityType.value as number],
                restrictedPosts: data.collaborativeContent.value
                  ? IsActivedValue.NO
                  : IsActivedValue.YES,
                restrictedComments: data.defaultEnableComments.value
                  ? IsActivedValue.NO
                  : IsActivedValue.YES,
                visibility: data.hide.value ? Visibility.HIDDEN : Visibility.VISIBLE,
                result: TryResult.SUCCESSFUL,
                statusCode: HttpCodes.OK,
                failureDescription: FailureDescription.NOT_FAILURE_RESULT_EVENT,
                componentSource: componentSource ?? ComponentSource.DEFAULT_SOURCE,
                communityPostModeration: data.moderationCommunity?.value
                  ? IsActivedValue.YES
                  : IsActivedValue.NO
              })
            })

            toastEventDispatch({
              type: ToastType.SUCCESS,
              message: t(
                'configuration_sidebar.access_and_privacy.toast.created_community_success'
              ),
              position: ToastPosition.BOTTOM_RIGHT
            })

            navigate(`community/${responseData.id}`)
          },
          onError(err) {
            toastEventDispatch({
              type: ToastType.DANGER,
              message: t('exception_state.service_unavailable.subtitle'),
              position: ToastPosition.BOTTOM_RIGHT
            })

            DataHubService.sendEvent({
              entity: DataHubEntities.Community,
              schema: new TryToCreateCommunitySchema({
                deviceInfo,
                communityName: data.name.value,
                communityType: CommunityTypeProductConsume[data.communityType.value as number],
                userRole: user.role || UserInfoRole.VIEWER,
                restrictedPosts: data.collaborativeContent.value
                  ? IsActivedValue.NO
                  : IsActivedValue.YES,
                restrictedComments: data.defaultEnableComments.value
                  ? IsActivedValue.NO
                  : IsActivedValue.YES,
                visibility: data.hide.value ? Visibility.HIDDEN : Visibility.VISIBLE,
                result: TryResult.FAILURE,
                statusCode: err.statusCode ?? HttpCodes.INTERNAL_SERVER_ERROR,
                failureDescription: err?.message ?? FailureDescription.NO_MESSAGE,
                componentSource: componentSource ?? ComponentSource.DEFAULT_SOURCE,
                communityPostModeration: data.moderationCommunity?.value
                  ? IsActivedValue.YES
                  : IsActivedValue.NO
              })
            })
          }
        }
      )
    }
  }

  const labels = {
    add: {
      title: t('modal.create_community.create_title'),
      saveButton: t('modal.create_community.save_button')
    },
    edit: {
      title: t('modal.create_community.edit_title'),
      saveButton: t('modal.create_community.edit_save_button')
    }
  }

  return (
    <CustomModal show={isOpen} onClose={() => handleClickCloseModal(true)}>
      <ModalHeader className='_px-5 _pt-5 _pb-4'>
        <div className='_d-flex _align-items-baseline'>
          <p className='_text-4 _m-0 _mr-4 _text-gray-900'>{labels[modalType].title}</p>

          <Button
            variation='tertiary'
            className='_p-0 _d-flex _border-0 _bg-transparent _ml-auto'
            onClick={() => handleClickCloseModal(true)}>
            <span className='_sr-only'>{t('general.close')}</span>
            <Icon icon={faXmark} className='_w-5 _h-5' />
          </Button>
        </div>
      </ModalHeader>

      <ModalBody className='_p-0 _px-5'>
        <FormModal />

        {modalType === CommunitiesActions.ADD && <AccessAndPrivacyCommunity />}

        <ReadOnlyCommunitySelector />

        <BlockCommentsSelector />

        <HideCommunitySelector />

        {enableModerationCommunity && <ModerationCommunitySelector />}
      </ModalBody>

      <ModalFooter className='_px-5 _pb-5 _pt-5'>
        <div className='_d-flex _ml-auto'>
          <Button className='_mr-5' variation='default' onClick={() => handleClickCloseModal(true)}>
            {t('general.close')}
          </Button>

          <Button
            loading={createCommunityQuery.isLoading}
            disabled={!isValidFormData || createCommunityQuery.isLoading}
            onClick={handleClick}>
            {labels[modalType].saveButton}
          </Button>
        </div>
      </ModalFooter>
    </CustomModal>
  )
}

export default CommunityModalContent
